import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import deepMerge from 'deepmerge';
import { TransferAssetFlowReducerState } from '@providers/TransferAssetFlowProvider/useTransferAssetFlowState';
import {
  CroChain,
  CronosChain,
  Network,
  Wallet,
  TxStatus,
  BridgeTxNotification,
  EthChainId,
  SourceChain,
  CosmosHubChain,
  BridgeTxTableRow,
  TerraChain,
  JunoChain,
  AkashChain,
  CantoChain,
  IrisChain,
  CelestiaChain,
} from '@types';
import staticConfig from '@config/static-config';
import { EthGravity__factory, Gravity__factory } from '@config/abi/types';
import { ibcTransfer, getIbcChannelInfo } from '@lib/ibc';
import {
  amountToBaseAmount,
  hexToBech32,
  tokenAssetToDenom,
  generateSigner,
  convertAmountBN,
  convertDepositTxStatus,
  convertTransferTxStatus,
  getChainFromBridgeTxApi,
} from '@lib/utils';
import { getChainInfo as getCosmosChainInfo } from '@lib/cosmos-chain';
import { getExtraInfo as getCronosExtraInfo } from '@lib/cronos-chain';
import {
  ethToCronosTransfer,
  cronosToEthTransfer,
  sendToCronosEth,
  getCrc21AutoContract,
  getCrc21ContractDecimals,
  getCrc21Contract,
  cronosToIbcTransfer,
} from '@lib/gravity-bridge';
import {
  cronosToCryptoOrgTransfer,
  getCroBridgeContract,
} from '@lib/cro-bridge';
import { ethers } from 'ethers';
import { InvalidInputError, UnsupportedError } from '@errors';
import useTxPolling from '@hooks/useTxPolling';
import useNetworkEnv from '@hooks/useNetworkEnv';
import { TransactionResponse } from '@ethersproject/abstract-provider';
import { createCosmosTxReceiptWorkaround } from '@lib/bridge-txn-api';
import useLocalStorage from '@hooks/useLocalStorage';

const useSendTransaction = () => {
  const [isTestnet] = useNetworkEnv();
  const [bridgeTxNotifications, setBridgeTxNotifications] = useState<
    BridgeTxNotification[]
  >([]);
  const pollTx = useTxPolling();
  const [persistedCosmosTxRecords, setPersistedCosmosTxRecords] =
    useLocalStorage(
      'cosmos-tx-records',
      JSON.stringify({
        txRecords: [],
      } as { txRecords: Array<BridgeTxTableRow> })
    );
  // State helper functions

  const callTxPolling = (tx: string, bridgeTxId: string) => {
    pollTx(
      (response) => {
        setBridgeTxNotifications((prev) =>
          prev.map((notification) => {
            if (notification.bridgeTx.id !== bridgeTxId) return notification;
            return deepMerge(notification, {
              bridgeTx: {
                depositTx: {
                  status: convertDepositTxStatus(
                    response.result.status,
                    response.result.destinationTransactionId
                  ),
                  hash: response.result.sourceTransactionId,
                },
                transferTx: {
                  status: convertTransferTxStatus(response.result.status),
                  hash: response.result?.destinationTransactionId || null,
                  chain: getChainFromBridgeTxApi(
                    response.result?.destinationChain as SourceChain,
                    isTestnet
                  ),
                },
              },
            });
          })
        );
      },
      tx,
      { isTestnet }
    );
  };

  const setInitialPendingTx = ({
    bridgeTxId,
    wallets,
    formState,
    depositTxHash,
  }: {
    bridgeTxId: string;
    wallets: Wallet[];
    formState: TransferAssetFlowReducerState;
    depositTxHash: string;
  }) => {
    const [firstWallet, secondWallet] = wallets;
    setBridgeTxNotifications((prev) =>
      prev.concat([
        {
          isHidden: true,
          bridgeTx: {
            id: bridgeTxId,
            depositTx: {
              status: TxStatus.pending,
              network: formState.fromNetwork,
              chain: firstWallet.chain,
              asset: formState.tokenAsset,
              amount: formState.amount,
              hash: depositTxHash,
            },
            transferTx: {
              status: TxStatus.pending,
              network: formState.toNetwork,
              chain: null,
              asset: formState.tokenAsset,
              amount: formState.amount,
              hash: null,
            },
          },
        },
      ])
    );
  };

  // Workflow #1: Crypto.org to Cronos

  const sendCryptoOrgToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const croChain = isTestnet ? CroChain.testnet4 : CroChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(croChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, croChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(croChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(croChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    callTxPolling(txHash, bridgeTxId);

    return bridgeTxId;
  };

  // Workflow #2: Ethereum to Cronos

  const sendEthereumToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const wallet = wallets[0];
    const cronosChain = isTestnet
      ? EthChainId.CronosGravityTestnet
      : EthChainId.Cronos;
    const { tokenAsset, toAddress } = formState;

    const destinationAddress =
      '0x' + '000000000000000000000000' + toAddress.substring(2);

    const signer = await generateSigner(wallets[0]);
    const amount = convertAmountBN(
      Network.Ethereum,
      formState.amount,
      tokenAsset.decimals
    );

    let tx: TransactionResponse;

    // Native ETH -> WETH router
    if (tokenAsset.symbol === 'ETH') {
      const contractAddress =
        getCronosExtraInfo(cronosChain).contractAddresses.ethGravity;
      const ethGravityContract = EthGravity__factory.connect(
        contractAddress,
        signer
      );
      tx = await sendToCronosEth(amount, toAddress, ethGravityContract);

      // Normal Gravity bridge transfer
    } else {
      const contractAddress =
        getCronosExtraInfo(cronosChain).contractAddresses.gravity;
      const gravityContract = Gravity__factory.connect(contractAddress, signer);
      tx = await ethToCronosTransfer(
        amount,
        tokenAsset.address[wallet.chain],
        destinationAddress,
        gravityContract
      );
    }

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';
    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.ethereum,
            wallets[0].address,
            SourceChain.cronos,
            formState.toAddress,
            formState.amount.toString(),
            tokenAsset.symbol,
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #3: Cronos to Ethereum

  const sendCronosToEthereumTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const crc21ContractAddress: string = await getCrc21AutoContract(
      cronosChain,
      tokenAsset.address[isTestnet ? EthChainId.Goerli : EthChainId.Mainnet]
    );

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );
    const bridgeFees = ethers.utils.parseUnits(
      feeInfo?.amount || '0',
      crc21ContractDecimals
    );

    const tx = await cronosToEthTransfer(
      amount.sub(bridgeFees),
      toAddress,
      bridgeFees,
      isTestnet ? EthChainId.Goerli : EthChainId.Mainnet,
      crc21Contract
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.ethereum,
            toAddress,
            ethers.utils
              .formatUnits(amount.sub(bridgeFees), crc21ContractDecimals)
              .toString(),
            tokenAsset.symbol,
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #4: Cronos to Crypto.org

  const sendCronosToCryptoOrgTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress } = formState;
    const cronosChain = isTestnet
      ? EthChainId.CronosGravityTestnet
      : EthChainId.Cronos;
    const contractAddress =
      getCronosExtraInfo(cronosChain).contractAddresses.croBridge;
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      tokenAsset.symbol === 'CRO' ? 18 : tokenAsset.decimals
    );

    const signer = await generateSigner(wallets[0]);
    const croBridge = getCroBridgeContract(contractAddress, signer);
    const tx = await cronosToCryptoOrgTransfer(toAddress, amount, croBridge);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    callTxPolling(tx.hash, bridgeTxId);

    return bridgeTxId;
  };

  // Workflow #5: CosmosHub to Cronos

  const sendCosmosHubToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const cosmosHubChain = isTestnet
      ? CosmosHubChain.testnet
      : CosmosHubChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(cosmosHubChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, cosmosHubChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(cosmosHubChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(cosmosHubChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
      gas: 140000,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(txHash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cosmos,
            wallets[0].address,
            SourceChain.cronos,
            hexToBech32(formState.toAddress, cronosPrefix),
            (amount / 1000000).toString(),
            'ATOM',
            txHash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #6: Cronos to CosmosHub

  const sendCronosToCosmosHubTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;

    let crc21ContractAddress: string;
    if (wallets[0].chain in tokenAsset.address) {
      crc21ContractAddress = tokenAsset.address[wallets[0].chain];
    } else {
      crc21ContractAddress = await getCrc21AutoContract(
        cronosChain,
        tokenAsset.address[
          isTestnet ? CosmosHubChain.testnet : CosmosHubChain.mainnet
        ]
      );
    }

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );

    const channelId =
      staticConfig.ibc.channelInfo.cosmosHubToCronos[
        isTestnet ? 'mainnet' : 'testnet'
      ].channelId;
    const tx = await cronosToIbcTransfer(
      amount,
      toAddress,
      crc21Contract,
      channelId
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.cosmos,
            toAddress,
            (amount.toNumber() / 1000000).toString(),
            'ATOM',
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #7: Terra to Cronos

  const sendTerraToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const terraChain = isTestnet ? TerraChain.testnet : TerraChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(terraChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, terraChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(terraChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(terraChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(txHash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.terra,
            wallets[0].address,
            SourceChain.cronos,
            hexToBech32(formState.toAddress, cronosPrefix),
            (amount / 1000000).toString(),
            'LUNA',
            txHash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #8: Cronos to Terra

  const sendCronosToTerraTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;

    let crc21ContractAddress: string;
    if (wallets[0].chain in tokenAsset.address) {
      crc21ContractAddress = tokenAsset.address[wallets[0].chain];
    } else {
      crc21ContractAddress = await getCrc21AutoContract(
        cronosChain,
        tokenAsset.address[isTestnet ? TerraChain.testnet : TerraChain.mainnet]
      );
    }

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );

    const channelId =
      staticConfig.ibc.channelInfo.terraToCronos[
        isTestnet ? 'mainnet' : 'testnet'
      ].channelId;
    const tx = await cronosToIbcTransfer(
      amount,
      toAddress,
      crc21Contract,
      channelId
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.terra,
            toAddress,
            (amount.toNumber() / 1000000).toString(),
            'LUNA',
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #9: Juno to Cronos

  const sendJunoToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const junoChain = isTestnet ? JunoChain.testnet : JunoChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(junoChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, junoChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(junoChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(junoChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(txHash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.juno,
            wallets[0].address,
            SourceChain.cronos,
            hexToBech32(formState.toAddress, cronosPrefix),
            (amount / 1000000).toString(),
            'JUNO',
            txHash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #10: Cronos to Juno

  const sendCronosToJunoTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;

    let crc21ContractAddress: string;
    if (wallets[0].chain in tokenAsset.address) {
      crc21ContractAddress = tokenAsset.address[wallets[0].chain];
    } else {
      crc21ContractAddress = await getCrc21AutoContract(
        cronosChain,
        tokenAsset.address[isTestnet ? JunoChain.testnet : JunoChain.mainnet]
      );
    }

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );

    const channelId =
      staticConfig.ibc.channelInfo.junoToCronos[
        isTestnet ? 'mainnet' : 'testnet'
      ].channelId;
    const tx = await cronosToIbcTransfer(
      amount,
      toAddress,
      crc21Contract,
      channelId
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.juno,
            toAddress,
            (amount.toNumber() / 1000000).toString(),
            'JUNO',
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #11: Akash to Cronos

  const sendAkashToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const akashChain = isTestnet ? AkashChain.testnet : AkashChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(akashChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, akashChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(akashChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(akashChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(txHash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.akash,
            wallets[0].address,
            SourceChain.cronos,
            hexToBech32(formState.toAddress, cronosPrefix),
            (amount / 1000000).toString(),
            'AKT',
            txHash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #12: Cronos to Akash

  const sendCronosToAkashTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;

    let crc21ContractAddress: string;
    if (wallets[0].chain in tokenAsset.address) {
      crc21ContractAddress = tokenAsset.address[wallets[0].chain];
    } else {
      crc21ContractAddress = await getCrc21AutoContract(
        cronosChain,
        tokenAsset.address[isTestnet ? AkashChain.testnet : AkashChain.mainnet]
      );
    }

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );

    const channelId =
      staticConfig.ibc.channelInfo.akashToCronos[
        isTestnet ? 'mainnet' : 'testnet'
      ].channelId;
    const tx = await cronosToIbcTransfer(
      amount,
      toAddress,
      crc21Contract,
      channelId
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.akash,
            toAddress,
            (amount.toNumber() / 1000000).toString(),
            'AKT',
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #13: Canto to Cronos

  const sendCantoToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const cantoChain = isTestnet ? CantoChain.testnet : CantoChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(cantoChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, cantoChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(cantoChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(cantoChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
      gas: 180000,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(txHash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.canto,
            wallets[0].address,
            SourceChain.cronos,
            hexToBech32(formState.toAddress, cronosPrefix),
            (amount / 1000000000000000000).toString(),
            'CANTO',
            txHash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #14: Cronos to Canto

  const sendCronosToCantoTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;

    let crc21ContractAddress: string;
    if (wallets[0].chain in tokenAsset.address) {
      crc21ContractAddress = tokenAsset.address[wallets[0].chain];
    } else {
      crc21ContractAddress = await getCrc21AutoContract(
        cronosChain,
        tokenAsset.address[isTestnet ? CantoChain.testnet : CantoChain.mainnet]
      );
    }

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );

    const channelId =
      staticConfig.ibc.channelInfo.cantoToCronos[
        isTestnet ? 'mainnet' : 'testnet'
      ].channelId;
    const tx = await cronosToIbcTransfer(
      amount,
      toAddress,
      crc21Contract,
      channelId
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.canto,
            toAddress,
            formState.amount,
            'CANTO',
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #15: Iris to Cronos

  const sendIrisToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const irisChain = isTestnet ? IrisChain.testnet : IrisChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(irisChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, irisChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(irisChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(irisChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(txHash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.iris,
            wallets[0].address,
            SourceChain.cronos,
            hexToBech32(formState.toAddress, cronosPrefix),
            (amount / 1000000).toString(),
            'IRIS',
            txHash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #16: Cronos to Iris

  const sendCronosToIrisTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;

    let crc21ContractAddress: string;
    if (wallets[0].chain in tokenAsset.address) {
      crc21ContractAddress = tokenAsset.address[wallets[0].chain];
    } else {
      crc21ContractAddress = await getCrc21AutoContract(
        cronosChain,
        tokenAsset.address[isTestnet ? IrisChain.testnet : IrisChain.mainnet]
      );
    }

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );

    const channelId =
      staticConfig.ibc.channelInfo.irisToCronos[
        isTestnet ? 'mainnet' : 'testnet'
      ].channelId;
    const tx = await cronosToIbcTransfer(
      amount,
      toAddress,
      crc21Contract,
      channelId
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.iris,
            toAddress,
            (amount.toNumber() / 1000000).toString(),
            'IRIS',
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #17: Celestia to Cronos

  const sendCelestiaToCronosTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].signer ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }

    const celestiaChain = isTestnet
      ? CelestiaChain.testnet
      : CelestiaChain.mainnet;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;
    const cronosPrefix = isTestnet ? 'tcrc' : 'crc';

    const sourceRpcUrl = getCosmosChainInfo(celestiaChain).rpc;
    const denom = tokenAssetToDenom(formState.tokenAsset, celestiaChain);
    const amount = amountToBaseAmount(formState.amount, formState.tokenAsset);
    const ibcChannel = getIbcChannelInfo(celestiaChain, cronosChain);

    const params = {
      rpcUrl: sourceRpcUrl,
      coinType: getCosmosChainInfo(celestiaChain).bip44.coinType,
      senderAddress: wallets[0].address,
      receiverAddress: hexToBech32(formState.toAddress, cronosPrefix),
      wallet: wallets[0].signer,
      sourcePort: ibcChannel.sourcePort,
      sourceChannel: ibcChannel.sourceChannel,
      denom,
      amount,
    };

    const { transactionHash: txHash } = await ibcTransfer(params);

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: txHash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(txHash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.celestia,
            wallets[0].address,
            SourceChain.cronos,
            hexToBech32(formState.toAddress, cronosPrefix),
            (amount / 1000000).toString(),
            'TIA',
            txHash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  // Workflow #18: Cronos to Celestia

  const sendCronosToCelestiaTransaction = async (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    if (
      !wallets[0].active ||
      !formState.tokenAsset ||
      !formState.amount ||
      !formState.toAddress
    ) {
      throw new InvalidInputError(JSON.stringify({ wallets, formState }));
    }
    const { tokenAsset, toAddress, feeInfo } = formState;
    const cronosChain = isTestnet ? CronosChain.testnet : CronosChain.mainnet;

    let crc21ContractAddress: string;
    if (wallets[0].chain in tokenAsset.address) {
      crc21ContractAddress = tokenAsset.address[wallets[0].chain];
    } else {
      crc21ContractAddress = await getCrc21AutoContract(
        cronosChain,
        tokenAsset.address[
          isTestnet ? CelestiaChain.testnet : CelestiaChain.mainnet
        ]
      );
    }

    const signer = await generateSigner(wallets[0]);
    const crc21Contract = getCrc21Contract(crc21ContractAddress, signer);
    const crc21ContractDecimals = await getCrc21ContractDecimals(crc21Contract);
    const amount = convertAmountBN(
      Network.Cronos,
      formState.amount,
      crc21ContractDecimals
    );

    const channelId =
      staticConfig.ibc.channelInfo.celestiaToCronos[
        isTestnet ? 'mainnet' : 'testnet'
      ].channelId;
    const tx = await cronosToIbcTransfer(
      amount,
      toAddress,
      crc21Contract,
      channelId
    );

    const bridgeTxId = uuidv4();

    setInitialPendingTx({
      bridgeTxId,
      wallets,
      formState,
      depositTxHash: tx.hash,
    });

    // FIXME: Uncomment when indexing API is ready
    // callTxPolling(tx.hash, bridgeTxId);

    setPersistedCosmosTxRecords(
      JSON.stringify({
        txRecords: [
          ...JSON.parse(persistedCosmosTxRecords!).txRecords,
          createCosmosTxReceiptWorkaround(
            SourceChain.cronos,
            wallets[0].address,
            SourceChain.celestia,
            toAddress,
            (amount.toNumber() / 1000000).toString(),
            'TIA',
            tx.hash,
            isTestnet
          ),
        ],
      })
    );

    return bridgeTxId;
  };

  const sendTransaction = (
    wallets: Wallet[],
    formState: TransferAssetFlowReducerState
  ) => {
    const { fromNetwork, toNetwork } = formState;
    switch (fromNetwork + '|' + toNetwork) {
      case Network.CryptoOrg + '|' + Network.Cronos:
        return sendCryptoOrgToCronosTransaction(wallets, formState);
      case Network.Ethereum + '|' + Network.Cronos:
        return sendEthereumToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Ethereum:
        return sendCronosToEthereumTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.CryptoOrg:
        return sendCronosToCryptoOrgTransaction(wallets, formState);
      case Network.Cosmos + '|' + Network.Cronos:
        return sendCosmosHubToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Cosmos:
        return sendCronosToCosmosHubTransaction(wallets, formState);
      case Network.Terra + '|' + Network.Cronos:
        return sendTerraToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Terra:
        return sendCronosToTerraTransaction(wallets, formState);
      case Network.Juno + '|' + Network.Cronos:
        return sendJunoToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Juno:
        return sendCronosToJunoTransaction(wallets, formState);
      case Network.Akash + '|' + Network.Cronos:
        return sendAkashToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Akash:
        return sendCronosToAkashTransaction(wallets, formState);
      case Network.Canto + '|' + Network.Cronos:
        return sendCantoToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Canto:
        return sendCronosToCantoTransaction(wallets, formState);
      case Network.Iris + '|' + Network.Cronos:
        return sendIrisToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Iris:
        return sendCronosToIrisTransaction(wallets, formState);
      case Network.Celestia + '|' + Network.Cronos:
        return sendCelestiaToCronosTransaction(wallets, formState);
      case Network.Cronos + '|' + Network.Celestia:
        return sendCronosToCelestiaTransaction(wallets, formState);
      default:
        throw new UnsupportedError(fromNetwork + '|' + toNetwork);
    }
  };

  const showBridgeTxNotification = (id: string) =>
    setIsBridgeNotificationHidden(id, false);

  const hideBridgeTxNotification = (id: string) =>
    setIsBridgeNotificationHidden(id, true);

  const setIsBridgeNotificationHidden = (id: string, isHidden: boolean) =>
    setBridgeTxNotifications((prev) =>
      prev.map((notification) => {
        if (notification.bridgeTx.id === id) {
          return {
            ...notification,
            isHidden,
          };
        }

        return notification;
      })
    );

  return {
    sendTransaction,
    bridgeTxNotifications,
    showBridgeTxNotification,
    hideBridgeTxNotification,
  };
};

export default useSendTransaction;
